/* eslint-disable max-len */
/* eslint-disable quotes */
// The questions shown in the portal's Support window ("?" at the top right).
//
// Answers follow the clinician how-to guide (docs/portal_guide/), which is checked against
// the portal code. When a screen changes, update the guide page and then the answer here, so
// the FAQ, the guide and Perci never tell a clinician different things. Only describe what
// clinics see on the live portal (see the guide's README).
export type FaqItem = {
  question: string;
  answer: string;
};

export const questionAndAnswer: FaqItem[] = [
  {
    question: "What happens if I'm inactive on the clinician portal?",
    answer:
      "If you don't move the mouse, type, scroll or touch the screen in the portal for 60 minutes, a Session Timeout window warns that you will be signed out in 60 seconds. Click Stay Signed In to keep working, or Sign Out Now to sign out straight away; if you do nothing, you are signed out when the countdown ends. The timer is shared by all portal tabs in the same browser.",
  },
  {
    question: "How do I edit a patient's profile in the clinician portal?",
    answer:
      "On the Management page, Patients tab, type part of the patient's name or email address in the search box. Click the pencil icon in the Edit column of their row to open the Edit Patient window, make your changes and click Save. You can also click the pencil next to the patient's name on their own page. Email can't be changed here; use Contact us (the envelope icon) to change it.",
  },
  {
    question: 'Can I modify sets, reps, hold time, and rest time in a treatment plan?',
    answer:
      'Yes. In the plan form on the Treatment Plans tab (New or Update), click an exercise row under Selected Activities to open it, then set Sets, Reps per Set, Hold (sec) and Rest (sec); only the fields that apply to that exercise are shown. The expand/collapse button next to Selected Activities opens or closes all rows at once.',
  },
  {
    question: 'What do Top and All mean in the Session comparison table on the Sessions tab?',
    answer:
      "The Session comparison table compares each result from the selected day with the patient's previous attempt at the same exercise. Top, the default, shows only results that changed enough to matter, meaning by more than the smallest change that is clinically meaningful for that measurement. All shows every result from that day, including results that barely changed and exercises done for the first time.",
  },
  {
    question: 'How are skipped activities displayed in the Sessions tab?',
    answer:
      'An exercise skipped before it started shows an orange Skipped badge, and opening the row shows the reason if the patient gave one. In the Session comparison table, hover over the Compliance percentage to see Compliance Gaps, which lists each exercise below 100% with its reason and plan exercises that were Not Started.',
  },
  {
    question: 'What if the patient aborts an exercise before completion?',
    answer:
      'On the Sessions tab, an exercise the patient stopped early keeps whatever was recorded, with a badge such as Partial Skip or Stopped by patient and the reason if given. If the patient restarted the exercise, the abandoned attempt is shown as its own row with a blue Patient restarted badge, and the new attempt appears as well. The patient gets partial credit for sets that did not finish.',
  },
  {
    question: 'How do I generate a login QR code for a patient?',
    answer:
      "Click the QR code icon to the left of the patient's name in the patient page header (it shows only when the patient has an email address). PT Copilot emails the patient a sign-in code and opens an Account Access window with the QR code. On the cart tablet, select QR Code Sign In and hold the code on your screen up to the tablet; the code expires after 15 minutes.",
  },
  {
    question: 'What should I do if I enter the wrong email or password?',
    answer:
      "You see a sign-in error that doesn't say which detail was wrong. Check your email address and password and try again, or use the forgot-password link on the Sign In page to get an emailed code and set a new password.",
  },
  {
    question: "How do I reset a patient's password using the clinician portal?",
    answer:
      "A clinic admin does this from the Patients tab: click the three dots in the Actions column on the patient's row and choose Reset password, which emails the patient a code to set a new password. If the patient has never signed in, use Resend invite instead. To get a patient into the tablet without a password, click the QR code icon next to their name on their page.",
  },
  {
    question: 'How can I manage multiple treatment episodes for a patient?',
    answer:
      "Each treatment episode is a case. On the patient's page, use the View CaseID drop-down to switch cases, + Add new case… to create one, and Manage cases… to archive or reactivate one. Plans, sessions and notes are filed under the case selected in View CaseID, and Patient App CaseID sets which case's latest plan the patient's app shows.",
  },
  {
    question: 'How do I add a new patient to PT-Copilot?',
    answer:
      'On the Management page, Patients tab, click Add Patient at the top right. Fill in First Name, Last Name, Email and Height (required), plus any optional fields, then click Save; WebPT clinics can type the EMR Patient ID and click Import EMR info to fill in the details. PT Copilot emails the patient an invitation, and every new patient starts with case 001.',
  },
  {
    question: 'How can I see which clinician assigned a treatment plan?',
    answer:
      'On the Treatment Plans tab, each plan row shows Created by, the clinician who assigned the plan with the date and time.',
  },
  {
    question: 'Can I update a treatment plan after assigning it?',
    answer:
      "Yes, even while the patient is exercising. Click Update on the current plan, make your changes and click Save and Assign. During a live session you can change, add, remove or reorder exercises: the change goes straight to the patient's tablet, arrives before they reach that exercise, and asks them whether to start again if it is the exercise they are doing now. When no session is running, your changes replace the plan if the patient hasn't started it, or are saved as a new plan with a new Name if they have.",
  },
  {
    question: 'How do I manage treatment plans for different body parts?',
    answer:
      'In the plan form on the Treatment Plans tab, open the View dropdown in the exercise library and tick one or more body parts under Body Parts, such as Knees, Wrists or Upper Middle Back. Then add exercises with the + icon on each card and set them up for the patient.',
  },
  {
    question: 'How is Movement Feedback displayed?',
    answer:
      "They appear on the Sessions tab. Open an exercise row: a thick red line marked Movement Feedback on the movement graph shows moments when PT Copilot flagged the patient's form.",
  },
  {
    question: 'How do I read a Movement Feedback message?',
    answer:
      'On the Sessions tab, open an exercise row and point at the red Movement Feedback line on the movement graph to read the cue. Click the graph to jump the video to that moment.',
  },
  {
    question: 'How do I navigate between different case IDs for a patient?',
    answer:
      "Use the View CaseID drop-down at the top of the patient's page. The Treatment Plans, Progress, Sessions and Notes tabs all switch to the case you pick, and PT Copilot remembers your choice for that patient.",
  },
  {
    question: "Can I see a graph of a patient's progress over time?",
    answer:
      'Yes, on the Progress tab. Ask Perci for one in your own words, for example "show me his knee flexion over the last month". Perci answers with a draft graph; click Save to keep a graph. A saved graph keeps the readings from the day it was saved, is shared with every clinician who sees the patient, and is filed under the case in View CaseID. Click Refresh on a saved graph to save a new copy with the latest readings.',
  },
  {
    question: "What happens when I open a WebPT patient who isn't in PT Copilot yet?",
    answer:
      "When a link from WebPT carries an EMR Patient ID that no PT Copilot patient has, the Add Patient window opens with the EMR Patient ID filled in; complete the details and click Save. If the case doesn't exist in PT Copilot yet, it is created for you.",
  },
  {
    question: 'Where can I find explanations for activity goals?',
    answer:
      'In the plan form on the Treatment Plans tab, open an exercise and look at its Goals table. The Measure column says how each value is measured, next to Goal, Unit, Range and Error Range.',
  },
  {
    question: 'How do I add case details for a patient?',
    answer:
      "On the patient's page, open the View CaseID drop-down and choose + Add new case…, then enter the New Case ID, an optional Name and the Case type, and click Create. For patients with an EMR Patient ID, the Edit Patient window also has a Cases & ICD-10 table where you can enter ICD-10 codes for cases created in PT Copilot.",
  },
  {
    question: 'Can I see case details when viewing treatment plans?',
    answer:
      "Yes. The Treatment Plans tab shows the plans of the case selected in View CaseID at the top of the patient's page, and an exported plan PDF lists its case.",
  },
  {
    question:
      'What happens when I log in to the clinician portal via a patient’s page in an EMR?',
    answer:
      "A link from WebPT that carries the patient's WebPT patient ID and case ID opens that patient's page directly, on that case, starting on the Treatment Plans tab. If no PT Copilot patient has that EMR Patient ID yet, the Add Patient window opens with it filled in instead.",
  },
  {
    question: 'How are partially completed exercises displayed?',
    answer:
      "On the Sessions tab, an exercise that was not done normally shows a status badge such as Partial Skip, Stopped by patient or Stopped (inactivity), with the patient's Reason if given. Open the row to see a Partially Completed column with the reason, and sets that did not finish marked with a grey asterisk.",
  },
  {
    question: 'Can I set which body side faces the camera for exercises?',
    answer:
      "Yes, for side-view exercises that work both sides of the body together, such as squats. For these the patient stands or lies side-on to the tablet. On the exercise's row in the plan, choose Facing Camera, L or R: the side of the patient's body that will face the camera. Pick the side you want the camera to see best. An exercise done on one side has Body Side instead, which is the side the patient works.",
  },
  {
    question: 'Are units displayed for exercise metrics?',
    answer:
      "Yes. On the Sessions tab, each exercise's results table has a Unit column, shown in your unit system. You choose metric or imperial in Settings, under Clinical Preferences.",
  },
  {
    question: 'Can I switch the body side assignment for treatment plans?',
    answer:
      'Yes. In the plan form, Swap Sides flips every left exercise to right and every right exercise to left. You can also set Body Side (L or R) on each one-sided exercise, and copy an exercise to give it to both sides.',
  },
  {
    question: 'Are logs containing PII or PHI sent to the cloud?',
    answer:
      'No. Our logs never contain patient information, and all the tools we work with are protected by a signed HIPAA agreement.',
  },
  {
    question: 'Can I see the estimated execution time for tasks?',
    answer:
      'Yes. In the plan form on the Treatment Plans tab, each exercise shows a Time Estimate, and the total for the whole plan is shown next to Selected Activities.',
  },
  {
    question: 'Can I set the duration for each movement?',
    answer:
      'Yes, for timed exercises. In the plan form on the Treatment Plans tab, a timed exercise such as Sit to Stand has Duration (sec), how long each set lasts, which you can set per patient.',
  },
];
